import { useQuery } from '@tanstack/react-query';
import { UpdateExpectedBookingsParams } from '@blisspointmedia/bpm-types/dist/ExpectedBookings';
import { CROSS_CHANNEL } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { SelectorOption } from '@Components/ModalEditWidget/modal-edit-constants';
import { awaitJSON, CrossChannelLambdaFetch, ToolsLambdaFetch } from '@src/utils/fetch-utils';
import {
  GOAL_NAMES,
  GoalRow,
  GoalSelectorOptions,
  GoalsSavePayload,
  MediaTypeSetting,
  OptionsKey
} from './goal-intake-constants';

// getSourceOfTruthOptions scopes its lookup to the cross-channel page's params.
const CROSS_CHANNEL_TEST_COMPANY_PARAMS = { channel: CROSS_CHANNEL, pageID: CROSS_CHANNEL };

export const goalRowsQueryKey = (company: string) => ['goalIntakeNewRows', company];

const fetchGoalRows = async (company: string): Promise<GoalRow[]> => {
  const res = await ToolsLambdaFetch('/getBudgetIntakeData', {
    params: { company, includeLegacy: true }
  });
  const { mappedBudgetEntries } = await awaitJSON<{ mappedBudgetEntries: GoalRow[] }>(res);
  return mappedBudgetEntries;
};

export const useGoalRows = (company: string): ReturnType<typeof useQuery<GoalRow[]>> =>
  useQuery({
    queryKey: goalRowsQueryKey(company),
    enabled: Boolean(company),
    queryFn: () => fetchGoalRows(company)
  });

export const customSegmentsQueryKey = (company: string) => ['goalIntakeNewCustomSegments', company];

// The company's custom segment columns (e.g. Channel, Platform, Brand).
export const useCustomSegments = (company: string): ReturnType<typeof useQuery<string[]>> =>
  useQuery({
    queryKey: customSegmentsQueryKey(company),
    enabled: Boolean(company),
    queryFn: async () => {
      const res = await ToolsLambdaFetch('/getBudgetCustomMapping', { params: { company } });
      const { segmentNames } = await awaitJSON<{ segmentNames: string[] }>(res);
      return segmentNames;
    }
  });

export const selectorOptionsQueryKey = (company: string) => [
  'goalIntakeNewSelectorOptions',
  company
];

export const useGoalSelectorOptions = (
  company: string
): ReturnType<typeof useQuery<GoalSelectorOptions>> =>
  useQuery({
    queryKey: selectorOptionsQueryKey(company),
    enabled: Boolean(company),
    queryFn: async () => {
      const [labelRes, sourceRes] = await Promise.all([
        ToolsLambdaFetch('/getBudgetIntakeLabelOptions', { params: { company } }),
        CrossChannelLambdaFetch('/getSourceOfTruthOptions', {
          params: { company, ...CROSS_CHANNEL_TEST_COMPANY_PARAMS }
        })
      ]);
      const [labelOptions, sourceOptions] = await Promise.all([
        awaitJSON<GoalSelectorOptions>(labelRes),
        awaitJSON<SelectorOption[]>(sourceRes)
      ]);
      return {
        ...labelOptions,
        [OptionsKey.SOURCE]: sourceOptions,
        [OptionsKey.GOAL_NAME]: GOAL_NAMES.map(name => ({ label: name, value: name }))
      };
    }
  });

export const saveGoals = async (payload: GoalsSavePayload): Promise<void> => {
  await ToolsLambdaFetch('/setBudgetIntakeData', { method: 'POST', body: payload });
};

// Legacy rows live in expected_bookings; save them the way the old Expected Bookings page does.
export const saveLegacyBookings = async (body: UpdateExpectedBookingsParams): Promise<void> => {
  await ToolsLambdaFetch('/update_expected_bookings', { method: 'POST', body });
};

export const mediaTypeSettingsQueryKey = (company: string) => [
  'goalIntakeNewMediaTypeSettings',
  company
];

// Goal granularity per media type: true = "Campaign and Segment Level", false = "Segment Level".
export const useMediaTypeSettings = (
  company: string
): ReturnType<typeof useQuery<Map<string, boolean>>> =>
  useQuery({
    queryKey: mediaTypeSettingsQueryKey(company),
    enabled: Boolean(company),
    queryFn: async () => {
      const res = await ToolsLambdaFetch('/getCampaignTypes', { params: { company } });
      const { mediaTypes, campaignLevelMediaTypes } = await awaitJSON<{
        mediaTypes: string[];
        campaignLevelMediaTypes: string[];
      }>(res);
      return new Map(
        mediaTypes.map(mediaType => [mediaType, campaignLevelMediaTypes.includes(mediaType)])
      );
    }
  });

export const saveMediaTypeSettings = async (
  company: string,
  mediaTypeSettings: MediaTypeSetting[]
): Promise<void> => {
  await ToolsLambdaFetch('/setCampaignTypes', {
    method: 'POST',
    body: { company, mediaTypeSettings }
  });
};
